import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  CircularProgress,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import privilegeService, { 
  getRolePrivileges, 
  saveRolePrivileges, 
  saveSubmenuPermissions,
  getSubmenuPermissions 
} from '../services/privilegeService';
import axiosInstance from '../utils/axios';

// Menu structure with submenus - use the same structure as in privilegeService
const menuStructure = [
  {
    id: 'DASHBOARD',
    text: 'Dashboard',
    path: '/dashboard',
    submenus: []
  },
  {
    id: 'INVENTORY',
    text: 'Inventory',
    path: '/inventory',
    submenus: [
      { id: 'INVENTORY_CATEGORIES', text: 'Categories', path: '/inventory/categories' },
      { id: 'INVENTORY_PRODUCTS', text: 'Products', path: '/inventory/products' },
      { id: 'INVENTORY_TAXES', text: 'Taxes', path: '/inventory/taxes' },
      { id: 'INVENTORY_UNITS', text: 'Units', path: '/inventory/units' }
    ]
  },
  {
    id: 'SALES',
    text: 'Sales',
    path: '/sales',
    submenus: [
      { id: 'SALES_ORDERS', text: 'Add Order', path: '/sales/pos' },
      { id: 'SALES_VIEW_ORDERS', text: 'View Orders', path: '/sales/view-orders' },
      { id: 'SALES_RETURNS', text: 'Returns', path: '/sales/returns' },
      { id: 'SALES_VIEW_RETURNS', text: 'View Returns', path: '/sales/view-returns' }
    ]
  },
  {
    id: 'PURCHASES',
    text: 'Purchases',
    path: '/purchases',
    submenus: [
      { id: 'PURCHASES_ORDERS', text: 'Orders', path: '/purchases/orders' },
      { id: 'PURCHASES_VIEW_ORDERS', text: 'View Orders', path: '/purchases/view-orders' },
      { id: 'PURCHASES_RETURNS', text: 'Returns', path: '/purchases/returns' }
    ]
  },
  {
    id: 'PEOPLE',
    text: 'People',
    path: '/people',
    submenus: [
      { id: 'PEOPLE_CUSTOMERS', text: 'Customers', path: '/people/customers' },
      { id: 'PEOPLE_SUPPLIERS', text: 'Suppliers', path: '/people/suppliers' },
      { id: 'PEOPLE_USERS', text: 'Users', path: '/people/users' }
    ]
  },
  {
    id: 'REPORTS',
    text: 'Reports',
    path: '/reports',
    submenus: [
      { id: 'REPORTS_SALES', text: 'Sales Reports', path: '/reports/sales' },
      { id: 'REPORTS_PURCHASES', text: 'Purchase Reports', path: '/reports/purchases' },
      { id: 'REPORTS_INVENTORY', text: 'Inventory Reports', path: '/reports/inventory' },
      { id: 'REPORTS_BILLING', text: 'Billing Reports', path: '/reports/billing' },
      { id: 'REPORTS_INVOICE_PROFIT_LOSS', text: 'Invoice P&L Reports', path: '/reports/invoice-profit-loss' },
      { id: 'REPORTS_CREDIT_CUSTOMERS', text: 'Manage Credit Customers', path: '/reports/credit-customers' }
    ]
  },
  {
    id: 'PRIVILEGES',
    text: 'Privileges',
    path: '/privileges',
    submenus: []
  },
  {
    id: 'SETTINGS',
    text: 'Settings',
    path: '/settings',
    submenus: []
  }
];

const RolePrivilegesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [roles] = useState(Object.keys(privilegeService.ROLES));
  const [selectedRole, setSelectedRole] = useState('');
  const [privileges, setPrivileges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [backendPrivileges, setBackendPrivileges] = useState(null);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePrivileges(selectedRole);
    }
  }, [selectedRole]);

  const fetchRolePrivileges = async (role) => {
    try {
      setLoading(true);
      
      // Get role privileges from the service (frontend localStorage)
      const { visibleMenus = [], accessibleRoutes = [] } = getRolePrivileges(role) || {};
      console.log('Fetched frontend privileges for role:', role, { visibleMenus, accessibleRoutes });
      
      // Convert to the format expected by the UI
      const menuPrivileges = convertPrivilegesToUIFormat(visibleMenus, accessibleRoutes);
      
      // Get submenu-specific permissions from localStorage
      const submenuPermissions = getSubmenuPermissions(role);
      console.log('Fetched submenu permissions for role:', role, submenuPermissions);
      
      // Apply submenu permissions to the UI privileges
      if (submenuPermissions.length > 0) {
        // Create a map for quick lookup
        const submenuMap = new Map();
        submenuPermissions.forEach(submenu => {
          submenuMap.set(submenu.id, submenu);
        });
        
        // Apply submenu permissions
        menuPrivileges.forEach(menu => {
          menu.submenus.forEach((submenu, index) => {
            const savedSubmenu = submenuMap.get(submenu.id);
            if (savedSubmenu) {
              // Override with saved permissions
              menu.submenus[index] = {
                ...menu.submenus[index],
                canView: savedSubmenu.canView,
                canCreate: savedSubmenu.canCreate,
                canEdit: savedSubmenu.canEdit,
                canDelete: savedSubmenu.canDelete
              };
              
              // If any submenu has view permission, ensure parent menu also has view permission
              if (savedSubmenu.canView) {
                menu.canView = true;
              }
            }
          });
          
          // Update parent menu view permission if all submenus are not visible
          if (menu.submenus.length > 0) {
            const allSubmenusHidden = menu.submenus.every(submenu => !submenu.canView);
            if (allSubmenusHidden) {
              menu.canView = false;
            }
          }
        });
      }
      
      setPrivileges(menuPrivileges);
      
      // Try to fetch backend privileges if available
      try {
        const response = await axiosInstance.get(`/role-privileges/${role}`);
        setBackendPrivileges(response.data);
        console.log('Fetched backend privileges for role:', role, response.data);
      } catch (error) {
        console.error('Backend privileges not available:', error);
        setBackendPrivileges(null);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching role privileges:', error);
      enqueueSnackbar('Failed to load role privileges', { variant: 'error' });
      setLoading(false);
    }
  };

  // Convert from the service format to the UI format
  const convertPrivilegesToUIFormat = (visibleMenus = [], accessibleRoutes = []) => {
    return menuStructure.map(menu => {
      // Main menu permissions
      const menuPermission = {
        id: menu.id,
        text: menu.text,
        path: menu.path,
        canView: visibleMenus.includes(menu.id),
        canCreate: visibleMenus.includes(menu.id),
        canEdit: visibleMenus.includes(menu.id),
        canDelete: visibleMenus.includes(menu.id),
        submenus: []
      };

      // Submenu permissions
      menuPermission.submenus = menu.submenus.map(submenu => {
        return {
          id: submenu.id,
          text: submenu.text,
          path: submenu.path,
          canView: visibleMenus.includes(submenu.id),
          canCreate: visibleMenus.includes(submenu.id),
          canEdit: visibleMenus.includes(submenu.id),
          canDelete: visibleMenus.includes(submenu.id)
        };
      });

      return menuPermission;
    });
  };

  // Convert from UI format back to service format
  const convertUIToServiceFormat = (privileges) => {
    const visibleMenus = [];
    const accessibleRoutes = [];

    privileges.forEach(menu => {
      if (menu.canView) {
        // Add main menu to visible menus
        visibleMenus.push(menu.id);
        
        // If the menu has a path, add it to accessible routes
        if (menu.path) {
          accessibleRoutes.push(menu.path);
          
          // For routes with nested paths, add wildcard permissions
          if (['SALES', 'PURCHASES', 'REPORTS', 'PEOPLE', 'INVENTORY'].includes(menu.id)) {
            accessibleRoutes.push(`${menu.path}/*`);
          }
        }
        
        // Process submenus
        menu.submenus.forEach(submenu => {
          if (submenu.canView) {
            // Add submenu ID to visible menus
            visibleMenus.push(submenu.id);
            
            // Add submenu path to accessible routes
            if (submenu.path) {
              accessibleRoutes.push(submenu.path);
              
              // Extract the parent path segment
              const pathParts = submenu.path.split('/');
              if (pathParts.length >= 2) {
                const parentPath = `/${pathParts[1]}`;
                
                // Add the parent path if it's not already in the list
                if (!accessibleRoutes.includes(parentPath)) {
                  accessibleRoutes.push(parentPath);
                }
                
                // Add wildcard for the parent path
                const wildcardPath = `${parentPath}/*`;
                if (!accessibleRoutes.includes(wildcardPath)) {
                  accessibleRoutes.push(wildcardPath);
                }
              }
            }
          }
        });
      }
    });
    
    // Add dashboard by default for all roles
    if (!accessibleRoutes.includes('/dashboard')) {
      accessibleRoutes.push('/dashboard');
      if (!visibleMenus.includes('DASHBOARD')) {
        visibleMenus.push('DASHBOARD');
      }
    }
    
    return { visibleMenus, accessibleRoutes };
  };

  // Convert UI privileges to backend format (roles)
  const convertUIToBackendFormat = (privileges) => {
    const modulePrivileges = [];
    const modulePermissions = {};
    
    // Initialize all main modules with no permissions
    const mainModules = [
      'DASHBOARD', 'INVENTORY', 'SALES', 'PURCHASES', 
      'CUSTOMERS', 'SUPPLIERS', 'USERS', 'REPORTS', 'SETTINGS'
    ];
    
    mainModules.forEach(module => {
      modulePermissions[module] = {
        module,
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false
      };
    });
    
    // Map menu structure to backend module names (parent module mapping)
    const menuToModule = {
      // Main modules
      'DASHBOARD': 'DASHBOARD',
      'INVENTORY': 'INVENTORY',
      'SALES': 'SALES',
      'PURCHASES': 'PURCHASES',
      'REPORTS': 'REPORTS',
      'SETTINGS': 'SETTINGS',
      'PRIVILEGES': 'SETTINGS',
      
      // People submodules to their parent modules
      'PEOPLE_CUSTOMERS': 'CUSTOMERS',
      'PEOPLE_SUPPLIERS': 'SUPPLIERS',
      'PEOPLE_USERS': 'USERS',
      
      // Inventory submodules to INVENTORY
      'INVENTORY_CATEGORIES': 'INVENTORY',
      'INVENTORY_PRODUCTS': 'INVENTORY',
      'INVENTORY_TAXES': 'INVENTORY',
      'INVENTORY_UNITS': 'INVENTORY',
      
      // Sales submodules to SALES
      'SALES_ORDERS': 'SALES',
      'SALES_VIEW_ORDERS': 'SALES',
      'SALES_RETURNS': 'SALES',
      'SALES_VIEW_RETURNS': 'SALES',
      
      // Purchases submodules to PURCHASES
      'PURCHASES_ORDERS': 'PURCHASES',
      'PURCHASES_VIEW_ORDERS': 'PURCHASES',
      'PURCHASES_RETURNS': 'PURCHASES',
      
      // Reports submodules to REPORTS
      'REPORTS_SALES': 'REPORTS',
      'REPORTS_PURCHASES': 'REPORTS',
      'REPORTS_INVENTORY': 'REPORTS',
      'REPORTS_BILLING': 'REPORTS',
      'REPORTS_INVOICE_PROFIT_LOSS': 'REPORTS',
      'REPORTS_CREDIT_CUSTOMERS': 'REPORTS'
    };

    // Process main menus and submenus - combine permissions by parent module
    privileges.forEach(menu => {
      // Process main menu
      const moduleKey = menuToModule[menu.id];
      if (moduleKey && modulePermissions[moduleKey]) {
        // Combine permissions (OR operation)
        modulePermissions[moduleKey].canView |= menu.canView;
        modulePermissions[moduleKey].canCreate |= menu.canCreate;
        modulePermissions[moduleKey].canEdit |= menu.canEdit;
        modulePermissions[moduleKey].canDelete |= menu.canDelete;
      }
      
      // Process submenus - update parent module permissions
      menu.submenus.forEach(submenu => {
        const submenuModuleKey = menuToModule[submenu.id];
        if (submenuModuleKey && modulePermissions[submenuModuleKey]) {
          // Only update if view is true (OR operation)
          if (submenu.canView) {
            modulePermissions[submenuModuleKey].canView = true;
            modulePermissions[submenuModuleKey].canCreate |= submenu.canCreate;
            modulePermissions[submenuModuleKey].canEdit |= submenu.canEdit;
            modulePermissions[submenuModuleKey].canDelete |= submenu.canDelete;
          }
        }
      });
    });
    
    // Convert modulePermissions object to array
    Object.values(modulePermissions).forEach(permission => {
      modulePrivileges.push(permission);
    });
    
    return {
      role: selectedRole,
      privileges: modulePrivileges
    };
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const handlePrivilegeChange = (menuIndex, field, submenuIndex = -1) => {
    setPrivileges(prevPrivileges => {
      const newPrivileges = [...prevPrivileges];
      
      if (submenuIndex === -1) {
        // Main menu privilege change
        newPrivileges[menuIndex] = {
          ...newPrivileges[menuIndex],
          [field]: !newPrivileges[menuIndex][field]
        };
        
        // If main menu view is unchecked, uncheck all submenu views too
        if (field === 'canView' && !newPrivileges[menuIndex][field]) {
          newPrivileges[menuIndex].submenus.forEach((submenu, idx) => {
            newPrivileges[menuIndex].submenus[idx].canView = false;
          });
        }
      } else {
        // Submenu privilege change
        newPrivileges[menuIndex].submenus[submenuIndex] = {
          ...newPrivileges[menuIndex].submenus[submenuIndex],
          [field]: !newPrivileges[menuIndex].submenus[submenuIndex][field]
        };
        
        if (field === 'canView') {
          if (newPrivileges[menuIndex].submenus[submenuIndex][field]) {
        // If a submenu view is checked, ensure parent menu view is checked
          newPrivileges[menuIndex].canView = true;
          } else {
            // If a submenu view is unchecked, check if any other submenu still has view permission
            const anySubMenuHasView = newPrivileges[menuIndex].submenus.some(
              (submenu, idx) => idx !== submenuIndex && submenu.canView
            );
            
            // Only uncheck parent menu view if no submenu has view permission
            if (!anySubMenuHasView) {
              newPrivileges[menuIndex].canView = false;
            }
          }
        }
      }
      
      return newPrivileges;
    });
  };

  const handleSavePrivileges = async () => {
    if (!selectedRole) {
      enqueueSnackbar('Please select a role', { variant: 'error' });
      return;
    }

    try {
      setSaveLoading(true);
      
      // Save submenu-specific permissions to localStorage
      const submenuPermissions = privileges.flatMap(menu => 
        menu.submenus.map(submenu => ({
          id: submenu.id,
          canView: submenu.canView,
          canCreate: submenu.canCreate,
          canEdit: submenu.canEdit,
          canDelete: submenu.canDelete
        }))
      );
      saveSubmenuPermissions(selectedRole, submenuPermissions);
      
      // Convert UI format to service format for main localStorage privileges
      const serviceFormat = convertUIToServiceFormat(privileges);
      console.log('Saving privileges for role:', selectedRole, serviceFormat);
      
      // Save using the privilege service for frontend localStorage
      const success = saveRolePrivileges(selectedRole, serviceFormat);
      
      // Always save to backend as well
        try {
          const backendFormat = convertUIToBackendFormat(privileges);
          console.log('Saving backend privileges:', backendFormat);
          
          await axiosInstance.put('/role-privileges/update', backendFormat);
        enqueueSnackbar('Role privileges saved successfully', { variant: 'success' });
        } catch (error) {
          console.error('Failed to save backend privileges:', error);
        console.error('Error details:', error.response?.data);
          enqueueSnackbar('Role privileges saved in frontend but failed to save in backend', { variant: 'warning' });
      }
      
      setSaveLoading(false);
    } catch (error) {
      console.error('Error saving role privileges:', error);
      enqueueSnackbar('Failed to save role privileges', { variant: 'error' });
      setSaveLoading(false);
    }
  };

  const handleRefresh = () => {
    if (selectedRole) {
      fetchRolePrivileges(selectedRole);
    }
  };

  const getUserRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'MANAGER':
        return 'Manager';
      case 'EXECUTIVE':
        return 'Executive';
      case 'BILLING_PERSON':
        return 'Billing Person';
      case 'USER':
        return 'Regular User';
      default:
        return role;
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Role-Based Privileges Management
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          This page manages privileges based on user roles rather than individual users.
          All users with the same role will share the same privileges.
        </Alert>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={handleRoleChange}
                  label="Select Role"
                  disabled={loading}
                >
                  <MenuItem value="" disabled>
                    -- Select a role --
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {getUserRoleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ mr: 1 }}
                disabled={!selectedRole || loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePrivileges}
                disabled={!selectedRole || loading || saveLoading}
              >
                {saveLoading ? <CircularProgress size={24} /> : 'Save Privileges'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {selectedRole && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Privileges for {getUserRoleLabel(selectedRole)}
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Menu</TableCell>
                        <TableCell align="center">View</TableCell>
                        <TableCell align="center">Create</TableCell>
                        <TableCell align="center">Edit</TableCell>
                        <TableCell align="center">Delete</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {privileges.map((menu, menuIndex) => (
                        <React.Fragment key={menu.id}>
                          <TableRow 
                            sx={{ 
                              '& > *': { fontWeight: 'bold' },
                              backgroundColor: '#f5f5f5'
                            }}
                          >
                            <TableCell>
                              {menu.text}
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canView}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canView')}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canCreate}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canCreate')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canEdit}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canEdit')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canDelete}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canDelete')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                          </TableRow>
                          
                          {/* Submenus */}
                          {menu.submenus.length > 0 && menu.submenus.map((submenu, submenuIndex) => (
                            <TableRow key={submenu.id}>
                              <TableCell sx={{ pl: 4 }}>
                                {submenu.text}
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canView}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canView', submenuIndex)}
                                  disabled={!menu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canCreate}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canCreate', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canEdit}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canEdit', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canDelete}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canDelete', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {backendPrivileges && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Backend Privileges Status</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="subtitle2" gutterBottom>
                        Backend privileges for role: {backendPrivileges.role}
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Module</TableCell>
                              <TableCell align="center">View</TableCell>
                              <TableCell align="center">Create</TableCell>
                              <TableCell align="center">Edit</TableCell>
                              <TableCell align="center">Delete</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {backendPrivileges.privileges.map((priv) => (
                              <TableRow key={priv.module}>
                                <TableCell>{priv.module}</TableCell>
                                <TableCell align="center">
                                  <Checkbox 
                                    checked={priv.canView} 
                                    disabled={true} 
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox 
                                    checked={priv.canCreate} 
                                    disabled={true} 
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox 
                                    checked={priv.canEdit} 
                                    disabled={true} 
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox 
                                    checked={priv.canDelete} 
                                    disabled={true} 
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="info">
                          This section shows the current privileges stored in the database. The checkboxes above show what you're configuring to save.
                        </Alert>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>
    </Layout>
  );
};

export default RolePrivilegesPage; 